/* The section's horizontal navigator (app.ts renderNavigator ~864): the record
   heading, the manager/record stamps, the help row, the File menu with its
   drop panel, and the area tabs. Hidden when no section owns the current view
   (the form views are full-page and belong to no list). Same DOM classes as
   styles.css; the stamp/help fragments come from the trusted help+derivation
   layer as HTML and are injected verbatim, exactly as the vanilla shell did. */
import type { JSX } from "react";
import { useAppState } from "./store";
import {
  currentSection,
  managerStampLine,
  openRecordName,
  recordStampLine,
  type SectionMenu,
} from "./sections";
import { aboutLine, iPanel, opTitle } from "./helpText";
import { RecordHeader } from "./RecordHeader";
import { ProductHeader } from "./ProductHeader";

/* a neutral wrapper that generates no box, so an injected fragment behaves as a
   direct child of .nav-bar / .nav-menus (parity with the vanilla innerHTML) */
const contents = { display: "contents" } as const;

export function Navigator(): JSX.Element | null {
  const { state, store } = useAppState();
  const section = currentSection(store);
  if (!section) return <div id="navigator" hidden />;

  const surface = state.me.surface;
  const heading = openRecordName(state) ?? section.label;
  /* "Out of the Menus" Part 1 (navigation-redesign.md NR2): with a project or
     portfolio-item record open on the redesign surface, the heading carries
     the stepper + action row and the File menu does not render — its verbs
     are relocated into the row's ⋯ overflow. Teams (no state machine) and the
     no-record section homes keep the File menu until Part 2 (NR7). */
  const openProject =
    state.view === "projects" && state.area
      ? state.projects.find((p) => p.id === state.openProjectId)
      : undefined;
  const redesign = store.on("nav-redesign") && openProject !== undefined;
  /* an open product's heading carries its phase chip, declaration stamp and
     Change phase… row (products-pdp.md PP18) — the record's one action surface */
  const openProduct =
    state.view === "products" && state.area
      ? state.products.find((p) => p.id === state.openProductId)
      : undefined;
  /* Part 2 (NR8): a directory section with no record open is the persistent
     table — its + New draft, search, and rail replace the File menu's verbs,
     so the menu does not render there. Teams (no directories) keeps its File
     menu; release keeps everything. */
  const tableHome = store.on("nav-redesign") && !state.area && section.dir !== undefined;
  const hideMenus = redesign || tableHome;
  const managerHtml = managerStampLine(state);
  const recordHtml = recordStampLine(state);
  /* helpRow (HL5): the section's About panel plus the record-states ⓘ wherever
     a state badge renders (keyed to recordStampLine, not the heading) */
  const helpParts = `${aboutLine(surface, section.label)}${recordHtml !== "" ? iPanel(surface, "record-states") : ""}`;

  const dropdown = (m: SectionMenu): JSX.Element | null =>
    state.openMenu !== m.label ? null : (
      <div className="nav-menu-panel" id="nav-menu-panel" aria-label={m.label}>
        {m.ops.map((o) => (
          <button
            key={o.label}
            className="nav-menu-item"
            title={opTitle(surface, o.label)}
            onClick={() => {
              store.toggleMenuBar(m.label); // the menu has served its purpose
              o.run();
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    );

  return (
    <div id="navigator">
      <div className="nav-bar">
        <h2 className="nav-section">{heading}</h2>
        {managerHtml && <div style={contents} dangerouslySetInnerHTML={{ __html: managerHtml }} />}
        {recordHtml && <div style={contents} dangerouslySetInnerHTML={{ __html: recordHtml }} />}
        {helpParts && <div className="nav-help" dangerouslySetInnerHTML={{ __html: helpParts }} />}
        {redesign && openProject && <RecordHeader project={openProject} />}
        {openProduct && <ProductHeader product={openProduct} />}
        {/* the menu band collapses when it would be empty (the table home has
            no menus and no tabs) — never an empty gray bar */}
        {(!hideMenus || section.tabs.length > 0) && (
        <div className="nav-menus">
          {!hideMenus &&
            section.menus.map((m) => (
            <div className="nav-menu-wrap" key={m.label}>
              <button
                className={"nav-menu" + (state.openMenu === m.label ? " open" : "")}
                aria-haspopup="menu"
                aria-controls="nav-menu-panel"
                aria-expanded={state.openMenu === m.label}
                onClick={() => store.toggleMenuBar(m.label)}
              >
                {m.label}
              </button>
              {dropdown(m)}
            </div>
          ))}
          {section.tabs.map((x) => (
            <button
              key={x.label}
              className={"nav-tab" + (state.area === x.label ? " selected" : "")}
              aria-current={state.area === x.label ? "page" : undefined}
              onClick={() => store.openArea(x.label)}
            >
              {x.label}
            </button>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}
